import { useMemo } from 'react';

import useBuildEstimate from '~/UI/pages/OrderSummaryPage/hooks/useBuildEstimate';
import { useEdges } from '~/api/easy-estimator-api/edges-api/useEdges';
import {
  IMAGE_COMING_SOON,
  WATERFALL_ICON,
  WATERFALL_ID,
} from '~/core/constants';
import { ProductType } from '~/core/enums';
import { ApplianceModel, ImagePreviewModel } from '~/core/models';
import { getProductDescription, imgUrl, isSomething } from '~/core/utils';
import {
  useFaucetContext,
  useOptionalProductContext,
} from '~/legacy/easy-estimator-page/context';

type OptionalImagePreviewModel = ImagePreviewModel | undefined;

const appliancesToImagePreviewModel = (
  appliance: ApplianceModel
): ImagePreviewModel => ({
  image: imgUrl(appliance?.beautyShotImageUrl ?? appliance.imageUrl),
  productName: getProductDescription(appliance),
  unitPrice: appliance.price || appliance.priceWithoutDiscount,
  unitOfMeasure: appliance.unitOfMeasure,
});

/**
 * Adds an item to a list only if it doesn't already exist based on productName comparison.
 *
 * This function prevents duplicate items from being added to the image preview list by
 * checking if an item with the same productName already exists. Only adds the item if
 * it's defined (passes isSomething check) and doesn't already exist in the list.
 *
 * @param listToFill - The existing array of image preview models to potentially add to
 * @param item - The optional image preview model to add if it doesn't already exist
 * @returns A new array with the item added (if conditions are met) or the original array
 */
const addIfNotInList = (
  listToFill: OptionalImagePreviewModel[],
  item?: OptionalImagePreviewModel
): OptionalImagePreviewModel[] => {
  const alreadyExists = listToFill.some(
    (pickedItem) => pickedItem && pickedItem.productName === item?.productName
  );

  return isSomething(item) && alreadyExists
    ? listToFill
    : [...listToFill, item];
};

export const useProductImages = () => {
  const estimate = useBuildEstimate();
  const { selectedFaucets } = useFaucetContext();
  const { sortedEdges: edges } = useEdges();
  const { optionalProducts } = useOptionalProductContext();
  const colorProductName =
    estimate.selectedColor && getProductDescription(estimate.selectedColor);
  const colorUnitPrice =
    estimate.selectedColor?.price ||
    estimate.selectedColor?.priceWithoutDiscount;
  const colorUnitOfMeasure = estimate.selectedColor?.unitOfMeasure;
  const previewImages = useMemo(
    () => [
      { image: estimate.canvasJpeg },
      {
        image: imgUrl(
          estimate.selectedColor?.slabImageUrl
            ? estimate.selectedColor.slabImageUrl.replace('-thumbnail', '')
            : IMAGE_COMING_SOON,
          600
        ),
        productName: colorProductName,
        unitPrice: colorUnitPrice,
        unitOfMeasure: colorUnitOfMeasure,
      },
      {
        image: imgUrl(
          estimate.selectedColor?.beautyShotImageUrl
            ? estimate.selectedColor.beautyShotImageUrl.replace(
                '-thumbnail',
                ''
              )
            : IMAGE_COMING_SOON,
          600
        ),
        productName: colorProductName,
        unitPrice: colorUnitPrice,
        unitOfMeasure: colorUnitOfMeasure,
      },
    ],
    [
      colorProductName,
      colorUnitOfMeasure,
      colorUnitPrice,
      estimate.canvasJpeg,
      estimate.selectedColor?.beautyShotImageUrl,
      estimate.selectedColor?.slabImageUrl,
    ]
  );

  const imagesFromAppliances = estimate.appliances
    .map(appliancesToImagePreviewModel)
    .reduce(addIfNotInList, []);

  // faucets are becoming more like other Appliances
  const imagesFromFaucets = Object.entries(selectedFaucets)
    .map<OptionalImagePreviewModel>(([productId, _selectedProperties]) => {
      const faucet = estimate.appliances.find(
        (appliance) => appliance.productId === Number(productId)
      );

      if (faucet) {
        return {
          image: imgUrl(faucet.beautyShotImageUrl || faucet.imageUrl, 600),
          productName: getProductDescription(faucet),
          unitPrice: faucet.price || faucet.priceWithoutDiscount,
          unitOfMeasure: faucet.unitOfMeasure,
        };
      } else {
        return undefined;
      }
    })
    .reduce(addIfNotInList, []);

  const imagesFromEdges =
    estimate.pricedProducts.map<OptionalImagePreviewModel>((item) => {
      const edge = edges.find(
        (element) => item.productId === element.productId
      );

      if (edge) {
        return {
          image: imgUrl(edge.imageUrl, 600),
          productName: getProductDescription(edge),
          unitPrice: edge.price || edge.priceWithoutDiscount,
          unitOfMeasure: edge.unitOfMeasure,
        };
      } else {
        return undefined;
      }
    });

  const waterFallImages = estimate.pricedProducts
    .filter((item) => item.lineItemId === WATERFALL_ID)
    .slice(0, 1) // pick the first match
    .map((waterFall) => ({
      image: imgUrl(WATERFALL_ICON, 600),
      productName: getProductDescription(waterFall),
      unitPrice:
        (waterFall.price || waterFall.priceWithoutDiscount) /
        waterFall.adjustedQty,
      unitOfMeasure: waterFall.unitOfMeasure,
    }))
    .reduce(addIfNotInList, []);

  const warrantyImages = estimate.pricedProducts
    .filter((item) => item.productType === ProductType.WARRANTY)
    .map<OptionalImagePreviewModel>((warranty) => {
      const catalogItem = optionalProducts.find(
        (item) => item.productId === warranty.productId
      );

      if (catalogItem) {
        return {
          image: imgUrl(catalogItem.imageUrl, 600),
          productName: getProductDescription(warranty),
          unitPrice: warranty.price || warranty.priceWithoutDiscount,
          unitOfMeasure: warranty.unitOfMeasure,
        };
      } else {
        return undefined;
      }
    });

  const images = useMemo(
    () =>
      [
        previewImages,
        imagesFromAppliances,
        imagesFromFaucets,
        imagesFromEdges,
        waterFallImages,
        warrantyImages,
      ]
        .flat()
        .filter(isSomething),
    [
      imagesFromAppliances,
      imagesFromEdges,
      imagesFromFaucets,
      previewImages,
      warrantyImages,
      waterFallImages,
    ]
  );

  return { images };
};

export default useProductImages;
